/***************************************************************************/
"use client";
/***************************************************************************/

import useVanillaSearchParams from "@/client/hooks/use-vanilla-search-params";
import { useRouter } from "next/navigation";
import Script from "next/script";
import { useEffect } from "react";

type GoogleAnalyticsProviderProps = {
  children: React.ReactNode;
  gaMeasurementId?: string;
};

const GoogleAnalyticsProvider: React.FC<GoogleAnalyticsProviderProps> = ({
  children,
  gaMeasurementId,
}) => {
  const { getSearchParams } = useVanillaSearchParams();
  const router = useRouter();

  const trackPageView = () => {
    if (window.gtag && gaMeasurementId) {
      const pathname = window.location.pathname;
      const searchParams = getSearchParams();
      const queryString = searchParams
        ? new URLSearchParams(
            Object.fromEntries(
              Object.entries(searchParams).filter(
                ([_, value]) => value !== undefined,
              ),
            ) as Record<string, string>,
          ).toString()
        : "";
      const fullPath = queryString ? `${pathname}?${queryString}` : pathname;

      window.gtag("config", gaMeasurementId, {
        page_path: fullPath,
      });
    }
  };

  useEffect(() => {
    trackPageView();

    const handleRouteChange = () => {
      trackPageView();
    };

    window.addEventListener("popstate", handleRouteChange);

    // Listen for Next.js App Router navigation
    const handleNavigation = () => {
      setTimeout(trackPageView, 100);
    };

    window.addEventListener("beforeunload", handleNavigation);
    window.addEventListener("popstate", handleNavigation);

    return () => {
      window.removeEventListener("popstate", handleRouteChange);
      window.removeEventListener("beforeunload", handleNavigation);
      window.removeEventListener("popstate", handleNavigation);
    };
  }, [gaMeasurementId]);

  if (!gaMeasurementId) {
    return children;
  }

  return (
    <>
      <Script
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`}
      />
      <Script
        id="gtag-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${gaMeasurementId}', {
              page_path: window.location.pathname,
            });
          `,
        }}
      />
      {children}
    </>
  );
};

export { GoogleAnalyticsProvider };
